/**
 * niftiDiscovery.ts
 *
 * Discover NIfTI files via the disk_items API.
 * Matching logic is an exact replica of old project's parseDiskItemType():
 *      Split path into directory + filename
 *      Iterate BRAINVISA_HIERARCHY, match with directoryRegex + patternRegex
 *      Extract infos (subject, acquisition, etc.) from directoryRegex capture groups
 */

import {
  NIFTI_FILE_REGISTRY,
  BRAINVISA_HIERARCHY,
  NiftiFileEntry,
  Modality,
} from "./niftiRegistry";

// ═══════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════

export interface DiscoveredFile {
  /** Matched registry entry */
  entry: NiftiFileEntry;
  /**  Full URL for loading */
  url: string;
  /** Raw path from disk_items API */
  rawPath: string;
  /**
   * Info extracted from directoryRegex capture groups
   * e.g.: { subject: "161_S16", acquisition: "ses-20231215" }
   */
  infos: Record<string, string>;
}

export interface DiscoveryResult {
  /** All available base images */
  bases: DiscoveredFile[];
  /** All available segmentations */
  segmentations: DiscoveredFile[];
  /** Currently active modality */
  activeModality: Modality;
  /** Quick lookup by setType */
  byKey: Map<string, DiscoveredFile>;
  /** All discovered files */
  allFiles: DiscoveredFile[];
  /** Extracted common infos */
  infos: Record<string, string>;
}

// ═══════════════════════════════════════════════════════════════
//  transformPath — ported from old project's DiskItems.jsx
//
//  ex: /data/1/1/1/ses-2024/filename.nii.gz
//    → /data/1/1/1/nii.gz/ses-2024/filename
//
//  NOTE: exported so other modules (e.g. useLongitudinal) can build
//  the same disk-item content URLs the viewer uses.
// ═══════════════════════════════════════════════════════════════

export function transformPath(path: string): string {
  const extension = ["nii.gz", "nii", "json"].find((ext) =>
    path.endsWith("." + ext),
  );
  if (!extension) return path;
  // Old project uses \d+/\d+/\d+, but subject folder may contain non-digits (e.g. 161_S16)
  return path
    .replace(/\/(\d+\/\d+\/[^/]+)\//, "/$1/" + extension + "/")
    .replace("." + extension, "");
}

// ═══════════════════════════════════════════════════════════════
//  fetchDiskItems — call the disk_items API
// ═══════════════════════════════════════════════════════════════

async function fetchDiskItems(
  baseUrl: string,
  patientId: string,
  acquisition: string,
): Promise<string[]> {
  const url = `${baseUrl}/patients/${patientId}/disk_items/${acquisition}`;

  const resp = await fetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!resp.ok) {
    throw new Error(
      `Failed to fetch disk items: ${resp.status} ${resp.statusText}`,
    );
  }

  const data = await resp.json();
  let files: string[] = [];
  if (Array.isArray(data)) files = data;
  else if (data?.items && Array.isArray(data.items)) files = data.items;
  else if (data?.disk_items && Array.isArray(data.disk_items))
    files = data.disk_items;
  else if (data?.files && Array.isArray(data.files)) files = data.files;

  return files;
}

function parseDiskItemType(relativePath: string): {
  infos: Record<string, string>;
  bv_type: string;
} | null {
  // Only process these formats
  if (
    ![".nii", ".nii.gz", ".json", ".pdf"].some((ext) =>
      relativePath.endsWith(ext),
    )
  ) {
    return null;
  }

  const chunks = relativePath.split("/");
  if (chunks.length < 2) {
    return null;
  }

  // Directory = all segments except last one (filename with extension)
  const directory = chunks.slice(0, chunks.length - 1).join("/");

  // Filename (with extension, since old code's patternRegex uses ^ to match filename)
  let pattern = chunks[chunks.length - 1];
  // Strip extension for matching (old code patterns don't include extensions)
  for (const ext of [".nii.gz", ".nii", ".json", ".pdf"]) {
    if (pattern.endsWith(ext)) {
      pattern = pattern.slice(0, -ext.length);
      break;
    }
  }

  // Iterate all hierarchy entries, dual regex match
  let candidate: string | null = null;

  for (const [bvType, typeInfo] of Object.entries(BRAINVISA_HIERARCHY)) {
    if (
      typeInfo.patternRegex.test(pattern) &&
      typeInfo.directoryRegex.test(directory)
    ) {
      candidate = bvType;
      break;
    }
  }

  if (!candidate) return null;

  // Extract infos from directoryRegex capture groups
  const candidateInfos = BRAINVISA_HIERARCHY[candidate];
  const groups = directory.match(candidateInfos.directoryRegex);
  if (!groups) return null;

  const infos: Record<string, string> = {};
  for (let i = 0; i < candidateInfos.infos.length; i++) {
    infos[candidateInfos.infos[i]] = groups[i + 1];
  }

  // Old project requires acquisition or acquisition_test/reference + subject
  if (
    (!infos.acquisition &&
      !infos.acquisition_test &&
      !infos.acquisition_reference) ||
    !infos.subject
  ) {
    console.warn(
      `[NIfTI Discovery] Subject or acquisition not found in path: ${relativePath}`,
    );
    return null;
  }

  return { infos, bv_type: candidate };
}

// ═══════════════════════════════════════════════════════════════
//   Main discovery function
// ═══════════════════════════════════════════════════════════════

export async function discoverNiftiFiles(
  baseUrl: string,
  patientId: string,
  acquisition: string,
): Promise<DiscoveryResult> {
  // Step 1: Fetch file list
  const allPaths = await fetchDiskItems(baseUrl, patientId, acquisition);
  // Step 2: Match each file (replica of parseDiskItems)
  const allFiles: DiscoveredFile[] = [];
  const byKey = new Map<string, DiscoveredFile>();
  let commonInfos: Record<string, string> = {};

  for (const rawPath of allPaths) {
    // disk_items API path format: /data/{orgId}/{patientId}/{subject}/...
    // Strip /data/{orgId}/{patientId}/ prefix to get relative path
    const prefixMatch = rawPath.match(/^\/data\/\d+\/\d+\//);
    const relativePath = prefixMatch
      ? rawPath.slice(prefixMatch[0].length)
      : rawPath.replace(/^\//, "");

    const parsed = parseDiskItemType(relativePath);
    if (!parsed) continue;

    // Find the corresponding entry in NIFTI_FILE_REGISTRY (with role/modality etc.)
    const entry = NIFTI_FILE_REGISTRY.find((e) => e.setType === parsed.bv_type);
    if (!entry) continue;

    //  Build loading URL
    const url = `${baseUrl}${transformPath(rawPath)}`;

    const discovered: DiscoveredFile = {
      entry,
      url,
      rawPath,
      infos: parsed.infos,
    };

    allFiles.push(discovered);
    byKey.set(parsed.bv_type, discovered);

    // Collect infos
    if (Object.keys(commonInfos).length === 0) {
      commonInfos = { ...parsed.infos };
    }
  }

  // Step 3: Categorize — filter by current acquisition
  const bases = allFiles
    .filter(
      (f) => f.entry.role === "base" && f.infos.acquisition === acquisition,
    )
    .sort((a, b) => {
      const aIsNative = a.rawPath.includes("native_space") ? 0 : 1;
      const bIsNative = b.rawPath.includes("native_space") ? 0 : 1;
      return aIsNative - bIsNative;
    });

  // Comparison-type files (e.g. WHASA 4D Labels / longitudinal) capture
  // acquisition_test / acquisition_reference instead of acquisition. Surface
  // them when the current acquisition is EITHER side of the comparison.
  const matchAcq = (f: DiscoveredFile) =>
    f.infos.acquisition === acquisition ||
    f.infos.acquisition_test === acquisition ||
    f.infos.acquisition_reference === acquisition;

  const segmentations = allFiles.filter(
    (f) =>
      (f.entry.role === "segmentation" || f.entry.role === "atlas") &&
      matchAcq(f),
  );

  const activeModality: Modality = bases.some(
    (b) => b.entry.modality === "t1mri",
  )
    ? "t1mri"
    : bases.some((b) => b.entry.modality === "t2flairmri")
    ? "t2flairmri"
    : bases[0]?.entry.modality ?? "t1mri";

  // Debug: log unmatched NIfTI files
  const niftiPaths = allPaths.filter(
    (p) => p.endsWith(".nii.gz") || p.endsWith(".nii"),
  );
  const matchedRawPaths = new Set(allFiles.map((f) => f.rawPath));
  const unmatched = niftiPaths.filter((p) => !matchedRawPaths.has(p));
  if (unmatched.length > 0) {
    console.warn(
      `[NIfTI Discovery] ⚠️ ${unmatched.length} unmatched NIfTI files:`,
      unmatched,
    );
  }

  return {
    bases,
    segmentations,
    activeModality,
    byKey,
    allFiles,
    infos: commonInfos,
  };
}
